import { useEffect, useState } from "react";
import { PageHeader } from "../components/EmptyState";
import { api, ApiError, type GoalDetail } from "../lib/api";

export default function Goals() {
  const [goal, setGoal] = useState<GoalDetail | null | "error">(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // form fields
  const [weight, setWeight] = useState("");
  const [date, setDate] = useState("");
  const [cal, setCal] = useState("");
  const [protein, setProtein] = useState("");
  const [steps, setSteps] = useState("");

  function load() {
    api
      .getGoals()
      .then((r) => setGoal(r.goal))
      .catch(() => setGoal("error"));
  }
  useEffect(load, []);

  function startEdit() {
    const g = goal && goal !== "error" ? goal : null;
    setWeight(g?.target_weight_kg != null ? String(g.target_weight_kg) : "");
    setDate(g?.target_date ?? "");
    setCal(g?.daily_calorie_target != null ? String(g.daily_calorie_target) : "");
    setProtein(g?.daily_protein_target_g != null ? String(g.daily_protein_target_g) : "");
    setSteps(g?.daily_steps_target != null ? String(g.daily_steps_target) : "");
    setErr(null);
    setEditing(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s));
    try {
      await api.postGoals({
        target_weight_kg: numOrNull(weight),
        target_date: date.trim() || null,
        daily_calorie_target: numOrNull(cal),
        daily_protein_target_g: numOrNull(protein),
        daily_steps_target: numOrNull(steps),
      });
      setEditing(false);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const g = goal && goal !== "error" ? goal : null;

  const rows: { label: string; value: string }[] = [
    { label: "Target weight", value: g?.target_weight_kg != null ? `${g.target_weight_kg} kg` : "—" },
    { label: "Target date", value: g?.target_date ?? "—" },
    { label: "Daily calories", value: g?.daily_calorie_target != null ? String(g.daily_calorie_target) : "—" },
    { label: "Daily protein", value: g?.daily_protein_target_g != null ? `${g.daily_protein_target_g} g` : "—" },
    { label: "Daily steps", value: g?.daily_steps_target != null ? String(g.daily_steps_target) : "—" },
  ];

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader
        title="Goals"
        eyebrow="Targets"
        action={
          !editing ? (
            <button type="button" className="btn-secondary" onClick={startEdit}>
              {g ? "Edit" : "Set goal"}
            </button>
          ) : null
        }
      />

      {editing ? (
        <form onSubmit={save} className="card-pad space-y-4">
          <Field label="Target weight (kg)">
            <input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} className="input" placeholder="80" />
          </Field>
          <Field label="Target date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" />
          </Field>
          <Field label="Daily calories (kcal)">
            <input inputMode="numeric" value={cal} onChange={(e) => setCal(e.target.value)} className="input" placeholder="2000" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Daily protein (g)">
              <input inputMode="numeric" value={protein} onChange={(e) => setProtein(e.target.value)} className="input" placeholder="150" />
            </Field>
            <Field label="Daily steps">
              <input inputMode="numeric" value={steps} onChange={(e) => setSteps(e.target.value)} className="input" placeholder="8000" />
            </Field>
          </div>
          {err && <div className="text-sm text-loss">{err}</div>}
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1 py-3" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
            <button className="btn-accent flex-1 py-3" disabled={busy}>
              {busy ? "Saving…" : "Save goal"}
            </button>
          </div>
        </form>
      ) : (
        <div className="card card-pad">
          <dl className="divide-y divide-stone-200">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                <dt className="field-label">{row.label}</dt>
                <dd className="font-display tabular-nums">{row.value}</dd>
              </div>
            ))}
          </dl>
          {!g && (
            <p className="mt-4 text-sm text-ink-subtle">
              No goal set yet — tap “Set goal” to add a target weight, date and daily targets.
              Your coach message and trends use these.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
